<template lang="pug">
	.audio-insertion--overdubbing-tool
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="720", :height="480", :autosize="true", :title="$tCaption('Overdubbing')", :closeOnOutsideClick="false")
			.audio-insertion--overdubbing-tool.popup.flex-col.space-large
				.flex-col.space-large(v-if="visibleSpeechResults.length", :class="{ 'disabled-controls': isGenerating }")
					.flex-col
						.font-small.upper.grey {{$tCaption('Select a word or a phrase to replace:')}}
						.font-big.flex-row.space.wrap
							.overdub-word(v-for="(word, idx) in visibleSpeechResults", :class="{ selecting: state === 1, selected: selected && idx >= minIdx && idx <= maxIdx, min: idx === minIdx, max: selectedRange.length == 2 && idx === maxIdx }", 
								@click="processSelect(word, idx)") {{word.SentenceWord}}
					.flex-col.field(v-if="selectedRange.length > 1")
						.font-small.upper.grey {{$tCaption('Enter new phrase:')}}
						DxTextBox(:value.sync="newPhrase", valueChangeEvent="input")
				Loader(v-if="isGenerating || isProcessing", text="", :fullscreen="true")
			template(slot="actions-right")
				DxButton(text="Save", type="default", @click="save", :disabled="!readyToSave || isGenerating")
				DxButton(text="Cancel", @click="popupVisible = false")
</template>

<script>
	import _ from 'lodash';
	import Popup from 'app-core/Popup.vue';
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import audioHelper from 'view/audioTracker/audio-helper';
	import Loader from 'app-core/Loader.vue'
	import audio from 'app-core/audio';

	export default {
		translationCategory: 'insertion.AudioOverdubbingTool',
		name: 'audio-insertion--overdubbing',
		components: { Popup, DxButton, Loader, DxTextBox },
		data() {
			return {
				popupVisible: false,
				isGenerating: false,
				track: null,
				isProcessing: 0,
				speechResults: [],
				startIdx: null,
				endIdx: null,
				state: 0,
				newPhrase: null
			}
		},
		computed: {
			selectedRange() {
				const res = [];
				if (this.startIdx !== null) {
					res.push(this.startIdx)
				}
				if (this.endIdx !== null) {
					res.push(this.endIdx)
				}
				return res;
			},
			minIdx() {
				return !this.selected ? null : _.min(this.selectedRange);
			},
			maxIdx() {
				return !this.selected ? null : _.max(this.selectedRange);
			},
			selected() {
				return this.endIdx !== null || this.startIdx !== null;
			},
			readyToSave() {
				return this.newPhrase?.length > 0;
			},
			visibleSpeechResults() {
				const endPos = this.track?.duration - this.track?.endOffset;
				return this.speechResults.filter(f => f.Offset + 0.1 >= this.track?.startOffset && (f.Offset + f.Duration - 0.1) <= endPos);
			}
		},
		watch: {

		},
		mounted() {
			if (__DEBUG__)
				window.audioOverdubbingTool = this;
		},
		destroyed() {
		},
		methods: {
			processSelect(word, idx) {
				if (this.state === 0) {
					this.startIdx = idx;
					this.endIdx = null;
				} else {
					this.endIdx = idx;
				}
				this.state = (this.state + 1) % 2;
			},
			show(track) {
				this.popupVisible = false;
				this.track = track;
				this.startIdx = null;
				this.endIdx = null;
				this.speechResults = [];
				this.newPhrase = null;
				setImmediate(() => {
					this.popupVisible = true;
					this.processAudio();
				});
			},
			async cutLimited(buffer, startIn, endIn, isStart) {
				let limitSeconds = 55;
				let limitSize = 1024 * 1024 * 4.5;

				let result = null;
				
				let start = startIn;
				let end = endIn;

				let processResult = async () => {
					if (isStart) {
						start = (end - start) > limitSeconds ? (endIn - limitSeconds) : start;
					} else {
						end = (end - start) > limitSeconds ? (startIn + limitSeconds) : end;
					}

					result = await audio.cut(buffer, start, end);

					if (__DEBUG__)
						console.debug('Cut limited', { buffer, start, end, result });

					return result;
				}

				await processResult();

				while (result?.length && result.length > limitSize) {
					limitSeconds /= 2;
					await processResult();
				}

				return result;
			},
			async processOverdub() {
				this.isGenerating = true;
				try {
					const startWord = this.visibleSpeechResults[this.minIdx];
					const endWord = this.visibleSpeechResults[this.maxIdx];

					const startPos = startWord.Offset;
					const endPos = endWord.Offset + endWord.Duration;

					const buffer = this.track.__buffer;
					
					if (__DEBUG__)
						console.log('Overdub', { startWord, endWord, startPos, endPos });

					const prefixBuffer = startPos > 0 ? await this.cutLimited(buffer, Math.max(0, this.track?.startOffset - 20), startPos, true) : null;
					const prefixText = this.visibleSpeechResults.filter((f, i) => i < this.startIdx).map(f => f.SentenceWord).join(' ');
					const prefixWav = prefixBuffer && await audioHelper.resampleBlob(await audio.bufferToWave(prefixBuffer), 24000, { mono: true });

					const suffixBuffer = endPos < buffer.duration ? await this.cutLimited(buffer, endPos, buffer.duration - Math.max(0, this.track?.endOffset - 20), false) : null;
					const suffixText = this.visibleSpeechResults.filter((f, i) => i > this.endIdx).map(f => f.SentenceWord).join(' ');
					const suffixWav = suffixBuffer && await audioHelper.resampleBlob(await audio.bufferToWave(suffixBuffer), 24000, { mono: true });

					const promises = [];

					if (prefixWav) {
						promises.push(this.$azure.uploadBlobWithInfoResponse(`overdubbing_${this.$Data.uuid()}.wav`, prefixWav, 'other'));
					} else {
						promises.push(Promise.resolve(null));
					}

					if (suffixWav) {
						promises.push(this.$azure.uploadBlobWithInfoResponse(`overdubbing_${this.$Data.uuid()}.wav`, suffixWav, 'other'));
					} else {
						promises.push(Promise.resolve(null));
					}
					
					if (!promises.length) return null;

					const azureFiles = await Promise.all(promises);
					const links = azureFiles.map(f => `${f?.AzureFileUrl}`);

					const language = this.track?.tracker?.language || 'en-US';

					const prefixInfo = prefixWav && `prefixAudioContent="${this.$Data.encodeHTML(links[0])}" prefixText="${prefixText || 'none'}"` || '';
					const suffixInfo = suffixWav && `suffixAudioContent="${this.$Data.encodeHTML(links[1])}" suffixText="${suffixText || 'none'}"` || '';

					const tag = `<mstts:ttsembedding ${prefixInfo ? prefixInfo : ''} ${suffixInfo ? suffixInfo : ''} format="wav"><lang xml:lang="${language}"> ${this.newPhrase} </lang></mstts:ttsembedding>`;

					const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis"  xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="${language}">`
						+ `<voice xml:lang="${language}" name="en-US-ZeroshotOverDubNeural"> ${tag} </voice>`
						+ `</speak>`;

					if (__DEBUG__)
						console.debug('Process overdub', { startPos, endPos, prefixBuffer, suffixBuffer, prefixText, suffixText, prefixWav, suffixWav, links, tag, ssml });

					return {
						ssml,
						tag,
						links,
						info: {
							prefixText,
							prefixWav,
							prefixBuffer,
							suffixText,
							suffixWav,
							suffixBuffer
						},
						voice: 'en-US-ZeroshotOverDubNeural',
						language: language,
						start: (startWord?.Offset || 0) - this.track?.startOffset,
						startWord,
						endWord,
						duration: endPos - startPos
					};
				} finally {
					this.isGenerating = false;
				}
			},
			async save() {
				const result = await this.processOverdub();
				this.$emit('save', result, this.track);
				this.popupVisible = false;
			},
			async processAudio() {
				this.isProcessing++;
				const speech = (await this.track?.__recognizeSpeech().finally(() => this.isProcessing--))?.RecognizedTranscript || [];

				this.speechResults = speech.reduce((arr, sent) => {
					const splittedSentence = sent.Text?.split(' ');
					arr.push(...sent.Words.map((f, i) => ({
						...f,
						SentenceWord: splittedSentence[i]
					})))
					return arr;
				}, []);
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.audio-insertion--overdubbing-tool {
		&.popup {
			min-height: 60px;
		}

		.overdub-word {
			cursor: pointer;
			transition: 0.3s;
			user-select: none;

			&.selecting:not(.selected):not(:hover) {
				color: #cdcdcd;
			}

			&:hover, &.selected {
				color: $base-accent;
			}

			&.min:before {
				content: '[';
			}

			&.max:after {
				content: ']';
			}
		}
	}
</style>
